import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Award, BookOpen, Building, GraduationCap, Star, CheckCircle } from 'lucide-react';

interface MembershipType {
  id: string;
  name: string;
  price_eur: number;
  description: string;
  benefits: string[];
  discount_percentage: number;
}

const Index = () => {
  const [membershipTypes, setMembershipTypes] = useState<MembershipType[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  const fetchMembershipTypes = async () => {
    const { data, error } = await supabase
      .from('membership_types_2025_12_03_10_30')
      .select('*')
      .order('price_eur', { ascending: true });
    
    if (error) {
      console.error('Error fetching membership types:', error);
    } else {
      setMembershipTypes(data || []);
    }
  };

  const getMembershipIcon = (name: string) => {
    if (name.includes('Student')) return <GraduationCap className="h-6 w-6" />;
    if (name.includes('Academic')) return <BookOpen className="h-6 w-6" />;
    if (name.includes('Professional')) return <Users className="h-6 w-6" />;
    if (name.includes('Chartered')) return <Award className="h-6 w-6" />;
    if (name.includes('Training')) return <Building className="h-6 w-6" />;
    if (name.includes('Corporate')) return <Shield className="h-6 w-6" />;
    return <Star className="h-6 w-6" />;
  };

  const handleMemberLogin = () => {
    navigate('/member-login');
  };

  const handleAdminLogin = () => {
    navigate('/admin-login');
  };

  const handleJoinMembership = (membershipName: string) => {
    const routeMap: { [key: string]: string } = {
      'Student Member': '/student-member-signup',
      'Academic Member': '/academic-member-signup',
      'Professional Member': '/professional-member-signup',
      'Chartered Member': '/chartered-member-signup',
      'Training Centre': '/training-centre-signup',
      'Corporate Bronze': '/corporate-bronze-signup',
      'Corporate Silver': '/corporate-silver-signup',
      'Corporate Gold': '/corporate-gold-signup'
    };
    navigate(routeMap[membershipName] || '/join');
  };

  const handlePortalAccess = (membershipName: string) => {
    const routeMap: { [key: string]: string } = {
      'Student Member': '/student-portal',
      'Academic Member': '/academic-portal',
      'Professional Member': '/professional-portal',
      'Chartered Member': '/chartered-portal',
      'Training Centre': '/training-centre-portal',
      'Corporate Bronze': '/bronze-portal',
      'Corporate Silver': '/silver-portal',
      'Corporate Gold': '/gold-portal'
    };
    navigate(routeMap[membershipName] || '/member-login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary-glow/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            {/* CSI Logo */}
            <div className="mb-8">
              <img 
                src="./images/Black & Gold Converged Security Excellence Banner.png" 
                alt="Converged Security International Logo" 
                className="mx-auto h-24 md:h-32 lg:h-40 w-auto object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              CSI Membership Program
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Join a Global Network of Integrated Security Leaders
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg transition-all duration-300"
                onClick={() => window.open('https://convergedsecurity.es/onewebmedia/page_3.html', '_blank')}
              >
                Membership Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={handleMemberLogin}
              >
                Member Login
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">Welcome to Your Member Portal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Member Login
                </h3>
                <p className="text-muted-foreground">
                  Access your personalized dashboard, exclusive resources, course discounts, and networking opportunities. 
                  View your membership status, certificates, and digital badges.
                </p>
                <Button onClick={handleMemberLogin} className="w-full sm:w-auto">
                  Member Access
                </Button>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Administrator Dashboard
                </h3>
                <p className="text-muted-foreground">
                  Manage memberships, approve applications, track renewals, and oversee training center partnerships. 
                  Access analytics and member engagement metrics.
                </p>
                <Button onClick={handleAdminLogin} className="w-full sm:w-auto">
                  Admin Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Membership Tiers */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">Choose Your Membership Tier</CardTitle>
            <CardDescription className="text-lg max-w-4xl mx-auto">
              The Converged Security Institute connects professionals, academics, institutions, and corporate partners 
              committed to advancing excellence in strategic security leadership. Our members span physical, cyber, 
              operational, and regulatory domains, working together to shape the future of converged security.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {membershipTypes.slice(0, 4).map((membership) => (
                <Card key={membership.id} className="relative hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="flex justify-center mb-2 text-primary">
                      {getMembershipIcon(membership.name)}
                    </div>
                    <CardTitle className="text-lg">{membership.name}</CardTitle>
                    <div className="text-2xl font-bold text-primary">
                      €{membership.price_eur}/year
                    </div>
                    <CardDescription>{membership.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex flex-col gap-2">
                      <Button 
                        onClick={() => handleJoinMembership(membership.name)}
                        className="w-full"
                      >
                        Join Now
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handlePortalAccess(membership.name)}
                        className="w-full"
                      >
                        Portal
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Individual Membership Details */}
        {membershipTypes.slice(0, 4).map((membership) => (
          <Card key={`detail-${membership.id}`} className="mb-8">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="text-primary">
                  {getMembershipIcon(membership.name)}
                </div>
                <div>
                  <CardTitle className="text-2xl">{membership.name}</CardTitle>
                  <CardDescription className="text-lg">
                    €{membership.price_eur}/year - {membership.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold mb-3">Benefits Include:</h4>
                  <ul className="space-y-2">
                    {membership.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col justify-center space-y-4">
                  <Badge variant="secondary" className="w-fit">
                    {membership.discount_percentage}% Course Discount
                  </Badge>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      onClick={() => handleJoinMembership(membership.name)}
                      className="flex-1"
                    >
                      Join Now
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handlePortalAccess(membership.name)}
                      className="flex-1"
                    >
                      Access Portal
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Corporate & Training Centre Memberships */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">Institutional Memberships</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Training Centre */}
              {membershipTypes.find(m => m.name === 'Training Centre') && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Training Centre Partnership
                    </CardTitle>
                    <CardDescription>
                      €300/year - For Educational Institutions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Universities, colleges, or accredited training centres offering security-related programmes
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button onClick={() => handleJoinMembership('Training Centre')}>
                        Become an Approved Centre
                      </Button>
                      <Button variant="outline" onClick={() => handlePortalAccess('Training Centre')}>
                        Training Centre Portal
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Corporate Memberships */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Corporate Memberships
                  </CardTitle>
                  <CardDescription>
                    For companies and organisations in security, technology, or risk sectors
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-3 border rounded">
                      <div className="font-semibold">Bronze</div>
                      <div className="text-sm text-muted-foreground">€350/year</div>
                      <div className="text-xs">1-50 employees</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="font-semibold">Silver</div>
                      <div className="text-sm text-muted-foreground">€425/year</div>
                      <div className="text-xs">51-250 employees</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="font-semibold">Gold</div>
                      <div className="text-sm text-muted-foreground">€500/year</div>
                      <div className="text-xs">251+ employees</div>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/corporate-memberships')} className="w-full">
                    View Corporate Options
                  </Button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Comparison Table */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">Membership Benefits Comparison</CardTitle>
            <CardDescription>
              Compare what's included with each membership level to find the perfect fit for your professional journey.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benefits</TableHead>
                    <TableHead className="text-center">Student</TableHead>
                    <TableHead className="text-center">Academic</TableHead>
                    <TableHead className="text-center">Professional</TableHead>
                    <TableHead className="text-center">Chartered</TableHead>
                    <TableHead className="text-center">Training</TableHead>
                    <TableHead className="text-center">Corporate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Digital Badge & Certificate</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Course Discounts</TableCell>
                    <TableCell className="text-center">Up to 20%</TableCell>
                    <TableCell className="text-center">15%</TableCell>
                    <TableCell className="text-center">15%</TableCell>
                    <TableCell className="text-center">20%</TableCell>
                    <TableCell className="text-center">10%</TableCell>
                    <TableCell className="text-center">10%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Exclusive Webinars & Events</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Research Library Access</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">C-CSP Eligibility</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">Required</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Leadership Forums</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                    <TableCell className="text-center">✓</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Why Join CSI */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">Why Join CSI?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <Award className="h-12 w-12 text-primary" />
                </div>
                <h4 className="font-semibold">Strengthen Professional Credibility</h4>
                <p className="text-sm text-muted-foreground">
                  Gain recognition through CSI credentials and leadership platforms that distinguish you as a converged security expert.
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <Users className="h-12 w-12 text-primary" />
                </div>
                <h4 className="font-semibold">Expand Your Network</h4>
                <p className="text-sm text-muted-foreground">
                  Connect across disciplines and sectors with security professionals worldwide, building relationships that drive innovation.
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <Shield className="h-12 w-12 text-primary" />
                </div>
                <h4 className="font-semibold">Influence the Future</h4>
                <p className="text-sm text-muted-foreground">
                  Shape the future of integrated security governance through active participation in CSI initiatives and thought leadership.
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <BookOpen className="h-12 w-12 text-primary" />
                </div>
                <h4 className="font-semibold">Continuous Learning</h4>
                <p className="text-sm text-muted-foreground">
                  Access exclusive publications, research, and professional development opportunities that keep you at the forefront.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl mb-4">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">What are the benefits of CSI membership?</h4>
                  <p className="text-sm text-muted-foreground">
                    Members gain access to exclusive resources, networking opportunities, course discounts, and professional recognition.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">How do I apply for Chartered status (C-CSP)?</h4>
                  <p className="text-sm text-muted-foreground">
                    Professional members with demonstrated leadership can apply through their member portal after meeting eligibility requirements.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Can I upgrade my membership level?</h4>
                  <p className="text-sm text-muted-foreground">
                    Yes, members can upgrade at any time through their portal, with prorated pricing available.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Are there networking opportunities?</h4>
                  <p className="text-sm text-muted-foreground">
                    Yes, all members have access to webinars, roundtables, and exclusive events for professional networking.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card className="text-center">
          <CardContent className="py-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Join CSI?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Take the next step in your security career and join a global community of professionals dedicated to excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg transition-all duration-300"
                onClick={() => navigate('/join')}
              >
                Join CSI Today
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => window.open('https://convergedsecurity.es/onewebmedia/page_3.html', '_blank')}
              >
                Download Membership Guide
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
